import React, { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MiniTaskReplayButton from '../components/MiniTaskReplayButton';
import BalloonOption from './BalloonOption';
import { classroomWordUnits } from './ClassroomSchoolLetter';
import { GardenBackdrop } from '../components/RoundView';
import { newBalloonSession, nextBalloonChallenge } from './continuousBalloon';
import { playMiniTaskSound } from '../soundEffects';
import { setPronunciationPlayback } from './pronunciationPlayback';
import './balloonPop.css';
import './classroomBalloon.css';

export default function ClassroomBalloon({ data, pendingData, paused, onSettings, onClose }) {
  const [session, setSession] = useState(() => data.status === 'ready' ? newBalloonSession(data.entries) : null);
  const [phase, setPhase] = useState('speaking');
  const [popped, setPopped] = useState(-1);
  const [wrong, setWrong] = useState(-1);
  const [error, setError] = useState('');
  const [retryKey, setRetryKey] = useState(0);
  const audio = useRef(null);
  const locked = useRef(true);
  // Delay only the very first spoken target so the class can see the modal first.
  const initialAnnouncementPending = useRef(true);
  const latest = useRef(pendingData || data);
  latest.current = pendingData || data;
  const initial = useRef(data);
  useEffect(() => {
    if (data !== initial.current) {
      initial.current = data; setSession(data.status === 'ready' ? newBalloonSession(data.entries) : null);
      setPhase('speaking'); setError('');
    }
  }, [data]);
  useEffect(() => {
    if (!session && pendingData?.status === 'ready') {
      setSession(newBalloonSession(pendingData.entries)); setPhase('speaking'); setError('');
    }
  }, [pendingData, session]);
  const active = session?.pool.find(e => e.text === session.target);
  // Fit the longest visible word once; all three options share its letter size.
  const schoolRoundUnits = Math.max(1, ...(session?.slots.map(slot => classroomWordUnits(slot.word)) || []));

  useEffect(() => {
    locked.current = true;
    if (paused || !session || phase !== 'speaking') {
      if (!paused && session && phase === 'ready') locked.current = false;
      return undefined;
    }
    let live = true;
    let sound = null;
    let firstDelay = null;
    let audioTimeout = null;
    const fail = () => {
      if (live && sound) {
        sound.pause();
        setError('Hedef sesi çalınamadı. Hoparlör düğmesiyle tekrar deneyin.');
        setPhase('retry');
      }
    };
    const announce = () => {
      if (!live) return;
      initialAnnouncementPending.current = false;
      sound = new Audio(); audio.current = sound;
      sound.src = active?.audio.url || '';
      setPronunciationPlayback(sound);
      sound.onended = () => { if (live) { setError(''); setPhase('ready'); } };
      sound.onerror = fail;
      // Start the audio failure watchdog only when actual playback is attempted.
      audioTimeout = setTimeout(fail, 15000);
      try { Promise.resolve(sound.play()).catch(fail); } catch (_) { fail(); }
    };
    if (initialAnnouncementPending.current) firstDelay = setTimeout(announce, 1000);
    else announce();
    return () => {
      live = false;
      clearTimeout(firstDelay);
      clearTimeout(audioTimeout);
      if (sound) { sound.pause(); sound.onended = null; sound.onerror = null; }
    };
  }, [session, active, phase, paused, retryKey]);

  useEffect(() => {
    if (!paused && session && phase === 'ready') setPhase('speaking');
    // Re-announce the preserved target after returning from settings.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]);

  useEffect(() => {
    if (paused || !session || !['pop', 'wrong'].includes(phase)) return undefined;
    const timer = setTimeout(() => {
      if (phase === 'pop') setSession(old => nextBalloonChallenge(old, popped, latest.current.entries));
      setPopped(-1); setWrong(-1); setPhase('speaking');
    }, phase === 'pop' ? 440 : 560);
    return () => clearTimeout(timer);
  }, [phase, paused, session, popped]);

  const select = index => {
    if (locked.current || paused || phase !== 'ready') return;
    locked.current = true;
    if (session.slots[index].word === session.target) {
      setPopped(index); setPhase('pop'); playMiniTaskSound('correct', true);
    } else { setWrong(index); setPhase('wrong'); playMiniTaskSound('wrong', true); }
  };
  const replay = () => { if (!paused && ['ready', 'retry'].includes(phase)) { locked.current = true; setError(''); setPhase('speaking'); setRetryKey(n => n + 1); } };
  return <Dialog open fullScreen transitionDuration={0} fullWidth maxWidth={false} disableEscapeKeyDown
    aria-labelledby="classroom-balloon-title" className="classroom-balloon-dialog"
    BackdropProps={{ className: 'classroom-balloon-backdrop' }} PaperProps={{ className: 'classroom-balloon-modal' }}>
    <Box className="classroom-balloon balloon-pop" data-paused={paused ? 'true' : 'false'}>
      <GardenBackdrop />
      <Box className="classroom-balloon-inner">
        <Box className="classroom-balloon-header"><Typography component="h2" id="classroom-balloon-title">Balon Patlat</Typography>
          <Box><Button startIcon={<SettingsRoundedIcon />} disabled={paused} onClick={onSettings}>Ayarlar</Button><Button startIcon={<CloseRoundedIcon />} disabled={paused} onClick={onClose}>Kapat</Button></Box>
        </Box>
        {!session ? <Box className="classroom-balloon-unavailable"><Typography component="h2">Sesli içerik hazırlanıyor</Typography>
          <Typography>{latest.current.message || 'Bu harflerle en az üç farklı, onaylı sesli hedef henüz hazır değil.'}</Typography>
          <Button variant="contained" onClick={onSettings}>Öğrenilen Harfler</Button></Box> : <>
          <Box className="balloon-pop-stage" data-count={session.slots.length} role="group" aria-label="Balon seçenekleri"
            style={{ '--school-round-units': schoolRoundUnits }}>
            {session.slots.map((slot, index) => <BalloonOption key={index} slot={slot} fitLabel schoolLetters locked={paused || phase !== 'ready'}
              popping={popped === index} shaking={wrong === index} onSelect={() => select(index)} />)}
          </Box>
          <Box className="classroom-balloon-controls">
          <Box className="balloon-pop-listen"><MiniTaskReplayButton label="Hedefi yeniden dinle" className="balloon-pop-replay" tone="filled" disabled={paused || !['ready', 'retry'].includes(phase)} onClick={replay} />
            <Typography>Dinle ve doğru balonu patlat</Typography>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography role="status" className="classroom-balloon-status">{paused ? 'Oyun duraklatıldı.' : phase === 'wrong' ? '✕ Bu balon değil. Aynı hedefi tekrar dinleyelim.' : phase === 'pop' ? '✓ Doğru balon!' : phase === 'speaking' ? 'Hedefi dinleyelim…' : error ? 'Ses için yeniden dene.' : 'Doğru balona dokun.'}</Typography>
          </Box>
        </>}
      </Box>
    </Box>
  </Dialog>;
}
